import {logger} from '../../../logger';
import React, {useState, useRef, useCallback, useMemo, useEffect} from 'react';
import s from '../../s';
import * as util from '../../../util';
import ModalSimple from '../../../global/modal-simple';
import ReactCrop, {centerCrop, Crop, makeAspectCrop, PixelCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {runInAction} from 'mobx';
import {observer} from 'mobx-react';
import InfoIcon from '../shared/info-icon';
import {imageUploadStore} from './image-upload.store';
import {canvasToFile, pngNameFromPdf, renderCropToCanvas} from './image-upload.lifecycle';
import {uploadFeeLine} from './upload-fee-line';
import axios from 'axios';
import {getAntiForgeryToken} from '../../../util';

const centerAspectCrop = (width: number, height: number, aspect: number) => {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 100,
            },
            aspect,
            width,
            height,
        ),
        width,
        height,
    );
};

const ImageUpload: React.FC = observer(() => {
    const [clicked, setClicked] = useState<boolean>(false);
    const [imgSrc, setImgSrc] = useState<string>('');
    const [aspect, setAspect] = useState<number>(0);
    const [crop, setCrop] = useState<Crop | undefined>(undefined);
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | undefined>(undefined);
    const imgRef = useRef<HTMLImageElement>(null);
    const mountedRef = useRef(true);
    const readerRef = useRef<FileReader | null>(null);
    const canUpload = useMemo(() => clicked, [clicked]);

    const resetCropUi = useCallback(() => {
        setImgSrc('');
        setCrop(undefined);
        setCompletedCrop(undefined);
        setClicked(false);
    }, []);

    const closeCropModal = useCallback(() => {
        resetCropUi();
        imageUploadStore.cancel();
    }, [resetCropUi]);

    const reportCropReadFailure = useCallback(
        (requestId?: number) => {
            if (!imageUploadStore.failCropRead(requestId)) {
                return;
            }

            resetCropUi();
            util.hideAllModals();
        },
        [resetCropUi],
    );

    useEffect(() => {
        s.getPPIValuesForImageUpload();
    }, []);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;

            if (readerRef.current) {
                readerRef.current.abort();
                readerRef.current = null;
            }

            imageUploadStore.dispose();
        };
    }, []);

    const checkResoLevel = useCallback(() => {
        if (s.ppi_w_actual < s.ppi_w || s.ppi_h_actual < s.ppi_h) {
            imageUploadStore.reso_level = 2;
        } else if (s.ppi_w_actual < s.ppi_w_max || s.ppi_h_actual < s.ppi_h_max) {
            imageUploadStore.reso_level = 1;
        }
    }, []);

    const showImage = useCallback((file: File) => {
        if (!imageUploadStore.applyConfirmedCrop(file)) {
            return;
        }

        s.pulse();
    }, []);

    const save = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            const cropped = imageUploadStore.getConfirmedCropFile();
            const crop = imageUploadStore.preparedCrop;

            if (!cropped || !crop) return;

            runInAction(() => {
                s.ppi_w_actual = Math.round(crop.width);
                s.ppi_h_actual = Math.round(crop.height);
            });

            checkResoLevel();

            if (imageUploadStore.reso_level !== 2) {
                showImage(cropped);
            } else {
                closeCropModal();
            }
        },
        [checkResoLevel, showImage, closeCropModal],
    );

    const createCroppedImage = useCallback(
        async (cropData: PixelCrop) => {
            if (!cropData.width || !cropData.height) {
                return;
            }

            if (!imgRef.current || !imageUploadStore.file) {
                reportCropReadFailure();
                return;
            }

            if (!imgRef.current.naturalWidth || !imgRef.current.naturalHeight) {
                return;
            }

            const sourceFile = imageUploadStore.file;
            const requestId = imageUploadStore.beginCropPrepare();
            setCompletedCrop(undefined);

            try {
                const rendered = renderCropToCanvas(imgRef.current, cropData);

                if (!rendered) {
                    reportCropReadFailure(requestId);
                    return;
                }

                const croppedFile = await canvasToFile(rendered.canvas, sourceFile.name, sourceFile.type);

                if (!mountedRef.current) {
                    return;
                }

                if (!croppedFile) {
                    reportCropReadFailure(requestId);
                    return;
                }

                if (!imageUploadStore.completeCropPrepare(croppedFile, requestId, rendered.naturalCrop)) {
                    return;
                }

                if (!mountedRef.current) {
                    return;
                }

                setCompletedCrop(rendered.naturalCrop);
            } catch (err) {
                logger.error(err);

                if (mountedRef.current) {
                    reportCropReadFailure(requestId);
                }
            }
        },
        [reportCropReadFailure],
    );

    const clear = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            imageUploadStore.removeUploadedImage();
            resetCropUi();
            s.pulse();
        },
        [resetCropUi],
    );

    const selectFile = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (!e.target.files || !e.target.files.length) {
                return;
            }

            let file = e.target.files[0];
            e.target.value = '';

            const replacingPreview = !!imageUploadStore.localObjectUrl || (typeof s.ps.override_d1_img === 'string' && s.ps.override_d1_img.startsWith('blob:'));
            const selectionId = imageUploadStore.beginFileSelection();
            resetCropUi();

            if (replacingPreview) {
                s.pulse();
            }

            if (readerRef.current) {
                readerRef.current.abort();
                readerRef.current = null;
            }

            const isPDF = file.type === 'application/pdf';

            if (isPDF) {
                try {
                    s.ui.image_uploading = true;

                    const url = '/api/PDFPNG/convert';
                    const config = {
                        headers: {
                            RequestVerificationToken: getAntiForgeryToken(),
                        },
                        responseType: 'blob' as const,
                    };
                    const response = await axios.post(url, imageUploadStore.getFormData(file), config);

                    if (!mountedRef.current || !imageUploadStore.isCurrentFileSelection(selectionId)) {
                        return;
                    }

                    const blob = response.data;

                    if (!blob) {
                        imageUploadStore.imageUploadError();
                        return;
                    }

                    file = new File([blob], pngNameFromPdf(file.name), {type: 'image/png'});
                } catch (err) {
                    logger.error(err);

                    if (!mountedRef.current || !imageUploadStore.isCurrentFileSelection(selectionId)) {
                        return;
                    }

                    imageUploadStore.imageUploadError(err);
                    return;
                } finally {
                    if (imageUploadStore.isCurrentFileSelection(selectionId)) {
                        s.ui.image_uploading = false;
                    }
                }
            }

            if (!mountedRef.current || !imageUploadStore.isCurrentFileSelection(selectionId)) {
                return;
            }

            imageUploadStore.file = file;
            setClicked(true);

            const reader = new FileReader();
            readerRef.current = reader;
            reader.addEventListener('load', () => {
                if (!mountedRef.current || !imageUploadStore.isCurrentFileSelection(selectionId)) {
                    return;
                }

                const result = reader.result?.toString() || '';

                if (!result) {
                    reportCropReadFailure();
                    return;
                }

                setImgSrc(result);
                util.modal('modal-image-crop');
                setCrop(undefined);
            });
            reader.addEventListener('error', () => {
                if (!mountedRef.current || !imageUploadStore.isCurrentFileSelection(selectionId)) {
                    return;
                }

                reportCropReadFailure();
            });
            reader.readAsDataURL(file);
        },
        [resetCropUi, reportCropReadFailure],
    );

    const handleCenterCrop = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const newAspect = s.ps.docWidth / s.ps.docHeight;
        setAspect(newAspect);
        if (newAspect) {
            const {width, height} = e.currentTarget;
            setCrop(centerAspectCrop(width, height, newAspect));
        }
    }, []);

    const handleImageDecodeError = useCallback(() => {
        reportCropReadFailure();
    }, [reportCropReadFailure]);

    // Upload & Print (FS 10214): the fee and whether the price already includes it come from the last /api/pricing response (the server rule), never from a client copy of it.
    const feeLine = uploadFeeLine(s.ui.upload_fee, s.ui.upload_fee_applied, s.prod.DocType);

    if (!s.ps.is_caf || !s.ui.show_cust || !s.ui.show_grad_year) {
        return (
            <div className="gray-box">
                <div style={{padding: '6px 0px 12px 0px'}}>
                    <div style={{fontWeight: 700}}>{s.prod.DocType} Upload</div>
                    {feeLine && (
                        <div
                            id="upload-print-fee"
                            style={{paddingTop: 4}}
                            aria-live="polite"
                        >
                            <span style={{fontWeight: 700}}>{feeLine.label}:</span> {feeLine.amount} <span style={{color: '#616161'}}>({feeLine.note})</span>
                        </div>
                    )}
                    <div style={{paddingTop: '6px'}}>
                        {!s.ui.image_uploaded && (
                            <input
                                id="imageUploadInput"
                                type="file"
                                accept="image/*, .pdf"
                                aria-label={`Upload ${s.prod.DocType}`}
                                onChange={selectFile}
                            />
                        )}
                        {s.ui.image_uploaded && (
                            <button
                                type="submit"
                                className="prod-atc-btn"
                                style={{fontSize: '1rem', padding: '6px 12px'}}
                                onClick={clear}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                    <div>
                        <ModalSimple
                            maxWidth={'800px'}
                            id="modal-image-crop"
                            title={'Image Crop'}
                            close={closeCropModal}
                        >
                            <div>
                                <p>Please crop the image first before proceeding.</p>
                                <div style={{textAlign: 'center'}}>
                                    {!!imgSrc && (
                                        <ReactCrop
                                            onChange={(_, percentCrop) => setCrop(percentCrop)}
                                            onComplete={cropData => createCroppedImage(cropData)}
                                            crop={crop}
                                            aspect={aspect}
                                            minHeight={100}
                                        >
                                            <img
                                                onLoad={handleCenterCrop}
                                                onError={handleImageDecodeError}
                                                ref={imgRef}
                                                src={imgSrc}
                                                alt="Crop Image"
                                            />
                                        </ReactCrop>
                                    )}
                                    <form
                                        id="form-fiu"
                                        onSubmit={save}
                                    >
                                        {imageUploadStore.preparingCrop && (
                                            <div
                                                role="status"
                                                aria-live="polite"
                                                style={{marginTop: '10px'}}
                                            >
                                                Preparing cropped image
                                            </div>
                                        )}
                                        {(!!completedCrop || imageUploadStore.preparingCrop) && canUpload && (
                                            <button
                                                type="submit"
                                                className="prod-atc-btn"
                                                style={{
                                                    fontSize: '1rem',
                                                    padding: '6px 12px',
                                                    marginTop: '10px',
                                                }}
                                                disabled={s.ui.image_uploading || !imageUploadStore.canConfirmCrop}
                                                aria-busy={imageUploadStore.preparingCrop}
                                                data-dismiss="modal"
                                            >
                                                Upload
                                            </button>
                                        )}
                                    </form>
                                </div>
                            </div>
                        </ModalSimple>
                    </div>

                    {!s.ui.image_uploaded && s.ui.image_uploaded_msg !== '' && (
                        <div style={{paddingTop: '6px'}}>
                            <div className="alert alert-danger">
                                <p>
                                    <b>Upload {s.prod.DocType} Image</b>
                                </p>
                                <p>{s.ui.image_uploaded_msg}</p>
                            </div>
                        </div>
                    )}

                    {imageUploadStore.reso_level === 2 && (
                        <div style={{paddingTop: '6px'}}>
                            <div className="alert alert-danger">
                                <p>
                                    <b>Image Resolution Error</b>
                                </p>
                                <p>
                                    A minimum resolution of {s.ppi_w}x{s.ppi_h} pixels is required for this frame. This baseline ensures that your photos maintain a basic level of clarity and detail when printed. For an enhanced visual experience, we strongly recommend uploading images with a
                                    resolution of at least {s.ppi_w_max}x{s.ppi_h_max} pixels. Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print. The resolution of the image uploaded is {s.ppi_w_actual}x{s.ppi_h_actual}.
                                </p>
                            </div>
                        </div>
                    )}

                    {imageUploadStore.reso_level === 1 && (
                        <div style={{paddingTop: '6px'}}>
                            <div className="alert alert-info">
                                <p>
                                    <span style={{fontSize: '1rem', fontWeight: 700}}>Image Size, Cropping and Aspect Ratio</span>
                                </p>

                                {imageUploadStore.reso_level > 0 && (
                                    <p style={{paddingTop: 6, paddingLeft: 12}}>
                                        <div
                                            className="modal-sub-header"
                                            style={{fontSize: '0.95rem'}}
                                        >
                                            Image size
                                        </div>
                                        Your image is{' '}
                                        <span style={{fontWeight: 700}}>
                                            {s.ppi_w}x{s.ppi_h} pixels
                                        </span>
                                        .
                                    </p>
                                )}

                                {imageUploadStore.reso_level === 1 && (
                                    <p style={{paddingTop: 6, paddingLeft: 12}}>
                                        <div
                                            className="modal-sub-header"
                                            style={{fontSize: '0.95rem'}}
                                        >
                                            Current print clarity
                                        </div>
                                        Your photo has the basic level of clarity and detail when printed.
                                    </p>
                                )}

                                {imageUploadStore.reso_level === 1 && (
                                    <p style={{paddingTop: 6, paddingLeft: 12}}>
                                        <div
                                            className="modal-sub-header"
                                            style={{fontSize: '0.95rem'}}
                                        >
                                            Optimal print quality
                                        </div>
                                        For an enhanced visual experience, we strongly recommend uploading images with a resolution of at least{' '}
                                        <span style={{fontWeight: 700}}>
                                            {s.ppi_w_max}x{s.ppi_h_max} pixels
                                        </span>
                                        . Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print.
                                    </p>
                                )}

                                {imageUploadStore.reso_level === 1 && (
                                    <p style={{paddingTop: 6, paddingLeft: 12}}>
                                        <div
                                            className="modal-sub-header"
                                            style={{fontSize: '0.95rem'}}
                                        >
                                            Printing at your selected size
                                        </div>
                                        This image can still be printed at your selected size, but it may appear slightly soft or less detailed when viewed up close. If you have a higher resolution version of this photo, uploading it will give the best result.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {imageUploadStore.reso_level === -22 && (
                        <div style={{paddingTop: 18}}>
                            <div>
                                <p>
                                    <span style={{fontSize: '1rem', fontWeight: 700}}>Image Size, Cropping and Aspect Ratio</span>
                                </p>

                                <p style={{paddingTop: 12, paddingLeft: 12}}>
                                    <div
                                        className="modal-sub-header"
                                        style={{fontSize: '0.95rem'}}
                                    >
                                        Minimum required
                                    </div>
                                    A minimum resolution of {s.ppi_w}x{s.ppi_h} pixels is required for this frame. This baseline ensures that your photos maintain a basic level of clarity and detail when printed.
                                </p>

                                <p style={{paddingTop: 6, paddingLeft: 12}}>
                                    <div
                                        className="modal-sub-header"
                                        style={{fontSize: '0.95rem'}}
                                    >
                                        Recommended for best results
                                    </div>
                                    For an enhanced visual experience, we strongly recommend uploading images with a resolution of at least{' '}
                                    <span style={{fontWeight: 700}}>
                                        {s.ppi_w_max}x{s.ppi_h_max} pixels
                                    </span>
                                    . Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print.
                                </p>

                                <p style={{paddingTop: 6, paddingLeft: 12}}>
                                    <div
                                        className="modal-sub-header"
                                        style={{fontSize: '0.95rem'}}
                                    >
                                        Accepted file types
                                    </div>
                                    Formats accepted: PNG (recommended), JPG or PDF
                                </p>
                            </div>
                        </div>
                    )}
                </div>
                <div>
                    <ModalSimple
                        id="modal-image-crop-info"
                        title={'Image Size, Cropping and Aspect Ratio'}
                    >
                        <div>
                            {imageUploadStore.reso_level !== 1 && (
                                <p>
                                    <div className="modal-sub-header">Minimum required</div>A minimum resolution of{' '}
                                    <span style={{fontWeight: 700}}>
                                        {s.ppi_w}x{s.ppi_h} pixels
                                    </span>{' '}
                                    is required for this frame. This baseline ensures that your photos maintain a basic level of clarity and detail when printed.
                                </p>
                            )}
                            {imageUploadStore.reso_level > 0 && (
                                <p>
                                    <div className="modal-sub-header">Image size</div>
                                    Your image is{' '}
                                    <span style={{fontWeight: 700}}>
                                        {s.ppi_w}x{s.ppi_h} pixels
                                    </span>
                                    .
                                </p>
                            )}
                            {imageUploadStore.reso_level === 1 && (
                                <p>
                                    <div className="modal-sub-header">Current print clarity</div>
                                    Your photo has the basic level of clarity and detail when printed.
                                </p>
                            )}
                            <p>
                                <div className="modal-sub-header">Optimal print quality</div>
                                For an enhanced visual experience, we strongly recommend uploading images with a resolution of at least{' '}
                                <span style={{fontWeight: 700}}>
                                    {s.ppi_w_max}x{s.ppi_h_max} pixels
                                </span>
                                . Higher resolution images allow for finer details to be captured and displayed, resulting in a more vibrant and lifelike print.
                            </p>
                            {imageUploadStore.reso_level === 1 && (
                                <p>
                                    <div className="modal-sub-header">Printing at your selected size</div>
                                    This image can still be printed at your selected size, but it may appear slightly soft or less detailed when viewed up close. If you have a higher resolution version of this photo, uploading it will give the best result.
                                </p>
                            )}
                            <p>
                                <div className="modal-sub-header">Accepted file types</div>
                                Formats accepted: PNG (recommended), JPG or PDF
                            </p>
                            {s.ui.upload_fee > 0 && <p>An Upload &amp; Print fee of {util.currency(s.ui.upload_fee)} is added to the item when you upload.</p>}
                        </div>
                    </ModalSimple>
                    <span style={{fontSize: '0.875rem'}}>
                        Image size, cropping and aspect ratio
                        <InfoIcon
                            id="modal-image-crop-info"
                            ariaLabel="View information about Image Size, Cropping and Aspect Ratio"
                            useMargin={true}
                        />
                    </span>
                </div>
            </div>
        );
    } else {
        return <div className="hide" />;
    }
});

export default ImageUpload;
